<script setup lang="ts">
import { clamp } from "lodash-es";
import { computed, nextTick, onUnmounted, useTemplateRef, watch } from "vue";

import { appZoomOpened, appZoomZoomChanged } from "@/analytics/events/menu";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import { maxZoom, minZoom } from "@/model/Settings";
import { useBoardStore } from "@/store/board";
import { useSelectionStore } from "@/store/selection";
import { TargetScroller, useZoomStore } from "@/store/zoom";
import { centerCoord } from "@/utils/dom/coordinates";
import { key } from "@/utils/shortcut/key";

import ZoomPopout from "./ZoomPopout.vue";

const ZOOM_TIMEOUT = 10;
let zoomTimeout = 0;
const active = computed({
  get: () => useZoomStore().dropdownOpen,
  set: (value) => useZoomStore().setDropdownOpen(value),
});
const zoomPopoutRef = useTemplateRef<InstanceType<typeof ZoomPopout>>("zoomPopout");
const toggleAppZoomRef = useTemplateRef("toggleAppZoom");

onUnmounted(() => (active.value = false));

const roundZoom = computed(() => Math.round(100 * useZoomStore().dynamicFactor));

// When the scale-stickies popup opens, close this one because they visually overlap
watch(
  () => useBoardStore().scaleDropdownOpen,
  (open) => {
    if (open && active.value) {
      active.value = false;
    }
  },
);

const plusKey = key("+", "altCtrl");
const minusKey = key("-", "altCtrl");

const { registerShortcut } = useShortcuts();
registerShortcut({ key: plusKey, exec: () => zoomStep(1.1) }, { repeat: true });
registerShortcut({ key: minusKey, exec: () => zoomStep(0.9) }, { repeat: true });

function zoomStep(step: number) {
  appZoom(step);
  setTimeout(() => appZoom(), ZOOM_TIMEOUT / 2); // Needed to prevent infinite zooming loop
}

async function toggle() {
  if (!active.value) {
    trackEvent(appZoomOpened());
  }

  active.value = !active.value;
  if (active.value) {
    await nextTick();
    zoomPopoutRef.value?.focusZoomIn();
  } else {
    toggleAppZoomRef.value?.focus();
  }
}

function close() {
  active.value = false;
  toggleAppZoomRef.value?.focus();
}

function updateLiveRegion() {
  const liveRegion = document.getElementById("zoomFactorLiveRegion");
  if (liveRegion) {
    liveRegion.textContent = `${roundZoom.value}%`;
  }
}

function appZoom(f?: number) {
  if (useSelectionStore().isSelectingWithRect) return;

  if (f) {
    const newFactor = clamp(useZoomStore().dynamicFactor * f, minZoom, maxZoom);
    if (newFactor !== useZoomStore().dynamicFactor) {
      if (!useZoomStore().zooming) {
        startZoom();
      }
      useZoomStore().setDynamicFactor(newFactor);
      updateLiveRegion();
      zoomTimeout = window.setTimeout(() => appZoom(f), ZOOM_TIMEOUT);
    } else if (useZoomStore().zooming) {
      stopZoom();
    }
  } else if (zoomTimeout) {
    stopZoom();
  }
}

function startZoom() {
  trackEvent(appZoomZoomChanged());
  useZoomStore().startZoom(new TargetScroller(centerCoord()));
}

function stopZoom() {
  clearTimeout(zoomTimeout);
  zoomTimeout = 0;
  useZoomStore().endZoom();
}

function shortcuts() {
  return plusKey.name() + " " + minusKey.name();
}
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions-->
  <div id="app-zoom" class="zoom" :class="{ active }" @keydown.esc="close">
    <BaseTooltip position="top">
      <BaseButton
        ref="toggleAppZoom"
        class="main-toggle"
        variant="ghost"
        color="grey"
        :activated="active"
        :aria-label="$t('label.menuTop.appZoom', { value: roundZoom })"
        :aria-expanded="active"
        @click="toggle"
      >
        {{ $t("general.percent", { value: roundZoom }) }}
      </BaseButton>
      <template #content>
        {{ $t("appZoom.zoom") }}
        <span class="shortcut">{{ shortcuts() }}</span>
      </template>
    </BaseTooltip>
    <Transition name="fade">
      <ZoomPopout
        v-if="active"
        ref="zoomPopout"
        class="zoom-controls"
        role="dialog"
        aria-modal="false"
        :aria-label="$t('appZoom.zoom')"
        :zoom-out-disabled="roundZoom <= 100 * minZoom"
        :zoom-in-disabled="roundZoom >= 100 * maxZoom"
        :zoom-out-label="$t('label.zoomOut')"
        :zoom-in-label="$t('label.zoomIn')"
        @zoom-out-start="appZoom(0.95)"
        @zoom-in-start="appZoom(1.05)"
        @zoom-end="appZoom()"
        @close="close"
      />
    </Transition>
    <div id="zoomFactorLiveRegion" aria-live="polite" aria-atomic="true" class="visually-hidden" />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";

#app-zoom.zoom {
  position: relative;
  display: flex;
  align-items: center;

  .main-toggle {
    font-weight: font.$weight-normal;
    font-size: font.$size-large;
    width: 4.5em;
    padding-left: 10px;
    padding-right: 10px;
  }

  .zoom-controls {
    right: 0;
    left: auto;
    transform: none;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    border: 0;
  }
}
</style>
